import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CalendarPlus, Images, MessageCircle, Clock, Check, CreditCard, ArrowRight, RotateCcw, ChevronDown, ChevronUp, Crown } from 'lucide-react';
import { api, getPublicImageUrl } from '../../utils/api';
import { getUser } from '../../utils/auth';
import { toast } from 'sonner';
import Header from '../shared/Header';
import Footer from '../shared/Footer';

const WHATSAPP_LINK = 'https://wa.me/2349160625644';

const STATUS_COLORS = {
  pending_payment: 'bg-gray-100 text-gray-700',
  pending: 'bg-yellow-100 text-yellow-800',
  awaiting_verification: 'bg-amber-100 text-amber-800',
  confirmed: 'bg-green-100 text-green-800',
  verified: 'bg-green-100 text-green-800',
  completed: 'bg-blue-100 text-blue-800',
  canceled: 'bg-red-100 text-red-800',
  cancelled: 'bg-red-100 text-red-800',
  unverified: 'bg-red-100 text-red-800',
};

const formatStatus = (s) => (s || '').split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
const getColor = (s) => STATUS_COLORS[s] || 'bg-gray-100 text-gray-700';

// Status tracker steps
const BOOKING_STEPS = [
  { key: 'submitted', label: 'Booking Submitted' },
  { key: 'payment_submitted', label: 'Payment Submitted' },
  { key: 'payment_verified', label: 'Payment Verified' },
  { key: 'confirmed', label: 'Appointment Confirmed' },
];

const getActiveStep = (booking) => {
  if (booking.booking_status === 'confirmed') return 4;
  if (booking.payment_status === 'verified') return 3;
  if (booking.booking_status === 'awaiting_verification') return 2;
  return 1;
};

const StatusTracker = ({ booking }) => {
  const active = getActiveStep(booking);
  return (
    <div className="flex items-center gap-1 sm:gap-2 w-full" data-testid="status-tracker">
      {BOOKING_STEPS.map((step, idx) => {
        const stepNum = idx + 1;
        const isComplete = stepNum < active;
        const isCurrent = stepNum === active;
        return (
          <React.Fragment key={step.key}>
            <div className="flex flex-col items-center flex-1 min-w-0">
              <div className={`h-7 w-7 sm:h-8 sm:w-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                isComplete ? 'bg-green-500 text-white' : isCurrent ? 'bg-[#C9A961] text-white' : 'bg-gray-200 text-gray-400'
              }`}>
                {isComplete ? <Check className="h-3.5 w-3.5" /> : stepNum}
              </div>
              <p className={`text-[9px] sm:text-[10px] mt-1 text-center leading-tight ${
                isComplete ? 'text-green-600 font-medium' : isCurrent ? 'text-[#C9A961] font-medium' : 'text-gray-400'
              }`}>{step.label}</p>
            </div>
            {idx < BOOKING_STEPS.length - 1 && (
              <div className={`h-0.5 flex-1 mt-[-18px] sm:mt-[-20px] min-w-2 ${isComplete ? 'bg-green-400' : 'bg-gray-200'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const BookingCard = ({ booking, expanded, onToggle, idx }) => {
  const navigate = useNavigate();
  return (
    <div className="bg-white rounded-xl border border-[#E6DCD3] overflow-hidden" data-testid={`booking-card-${idx}`}>
      <div className="p-4 sm:p-5 cursor-pointer hover:bg-[#FCFBF9] transition-colors" onClick={onToggle}>
        <div className="flex gap-3 sm:gap-4">
          {/* Image */}
          {booking.hairstyle_image && (
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden flex-shrink-0 bg-[#F3EBE1]">
              <img src={getPublicImageUrl(booking.hairstyle_image)} alt={booking.hairstyle_name} className="w-full h-full object-cover" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h3 className="font-serif text-base sm:text-lg text-[#3E2723] truncate">{booking.hairstyle_name}</h3>
                <p className="text-xs text-[#786E66] mt-0.5">Ref: {booking.booking_reference}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className={`px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium ${getColor(booking.booking_status)}`}>
                  {formatStatus(booking.booking_status)}
                </span>
                {expanded ? <ChevronUp className="h-4 w-4 text-[#786E66]" /> : <ChevronDown className="h-4 w-4 text-[#786E66]" />}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-[#786E66]">
              <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{booking.appointment_date} at {booking.appointment_time}</span>
              <span className="font-medium text-[#3E2723]">₦{booking.total_amount?.toLocaleString()}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="border-t border-[#E6DCD3] p-4 sm:p-5 bg-[#FCFBF9] space-y-4">
          <StatusTracker booking={booking} />

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            <div><span className="text-[#786E66] block mb-0.5">Date</span><span className="text-[#1A1A1A] font-medium">{booking.appointment_date}</span></div>
            <div><span className="text-[#786E66] block mb-0.5">Time</span><span className="text-[#1A1A1A] font-medium">{booking.appointment_time}</span></div>
            <div><span className="text-[#786E66] block mb-0.5">Amount</span><span className="text-[#1A1A1A] font-medium">₦{booking.total_amount?.toLocaleString()}</span></div>
            <div>
              <span className="text-[#786E66] block mb-0.5">Payment</span>
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${getColor(booking.payment_status)}`}>
                {formatStatus(booking.payment_status)}
              </span>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 pt-2">
            {booking.booking_status === 'pending_payment' && (
              <Link to={`/payment?booking=${booking.id}`} className="bg-[#3E2723] text-white hover:bg-[#2A1A17] rounded-full px-5 py-2 text-xs font-medium transition-all" data-testid={`make-payment-btn-${idx}`}>
                Make Payment
              </Link>
            )}
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="border border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white rounded-full px-5 py-2 text-xs font-medium transition-all inline-flex items-center gap-1.5" data-testid={`chat-admin-btn-${idx}`}>
              <MessageCircle className="h-3 w-3" /> Chat Admin
            </a>
            {(booking.booking_status === 'completed' || booking.booking_status === 'confirmed' || booking.booking_status === 'canceled' || booking.booking_status === 'cancelled') && (
              <button onClick={() => navigate(`/booking?hairstyle=${booking.hairstyle_id}`)} className="border border-[#C9A961] text-[#C9A961] hover:bg-[#C9A961] hover:text-white rounded-full px-5 py-2 text-xs font-medium transition-all inline-flex items-center gap-1.5" data-testid={`rebook-btn-${idx}`}>
                <RotateCcw className="h-3 w-3" /> Book Again
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const CustomerDashboard = () => {
  const [bookings, setBookings] = useState([]);
  const [hairstyles, setHairstyles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [expandedBooking, setExpandedBooking] = useState(null);
  const user = getUser();
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      api.getMyBookings(),
      api.getPublicHairstyles({ featured: true })
    ])
      .then(([bookingsRes, stylesRes]) => {
        setBookings(bookingsRes.data);
        setHairstyles(stylesRes.data.slice(0, 4));
        // Auto-expand the most recent upcoming booking
        const upcoming = bookingsRes.data.find(b => !['completed', 'canceled', 'cancelled'].includes(b.booking_status));
        if (upcoming) setExpandedBooking(upcoming.id);
      })
      .catch(() => toast.error('Failed to load dashboard data'))
      .finally(() => setLoading(false));
  }, []);

  const upcomingBookings = bookings.filter(b => !['completed', 'canceled', 'cancelled'].includes(b.booking_status));
  const completedBookings = bookings.filter(b => b.booking_status === 'completed');
  const cancelledBookings = bookings.filter(b => ['canceled', 'cancelled'].includes(b.booking_status));

  const tabData = {
    upcoming: upcomingBookings,
    completed: completedBookings,
    cancelled: cancelledBookings,
  };
  const tabs = [
    { key: 'upcoming', label: 'Upcoming', count: upcomingBookings.length },
    { key: 'completed', label: 'Completed', count: completedBookings.length },
    { key: 'cancelled', label: 'Cancelled', count: cancelledBookings.length },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FCFBF9] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#3E2723] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FCFBF9]" data-testid="customer-dashboard">
      <Header />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">

        {/* Welcome */}
        <div className="mb-8" data-testid="welcome-section">
          <h1 className="font-serif text-2xl sm:text-4xl text-[#3E2723] mb-1" data-testid="dashboard-title">
            Welcome, {user?.full_name}!
          </h1>
          <p className="text-sm sm:text-base text-[#786E66]">Manage your bookings and appointments</p>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-3 gap-3 sm:gap-5 mb-8 sm:mb-10" data-testid="quick-actions">
          <Link to="/gallery" className="group bg-white rounded-xl sm:rounded-2xl border border-[#E6DCD3] p-4 sm:p-6 hover:shadow-md hover:-translate-y-0.5 transition-all text-center" data-testid="action-book-appointment">
            <div className="h-10 w-10 sm:h-12 sm:w-12 bg-[#F3EBE1] rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-3 group-hover:bg-[#C9A961] group-hover:text-white transition-colors">
              <CalendarPlus className="h-5 w-5 sm:h-6 sm:w-6 text-[#3E2723] group-hover:text-white" />
            </div>
            <p className="text-xs sm:text-sm font-medium text-[#3E2723]">Book Appointment</p>
            <p className="text-[9px] sm:text-xs text-[#786E66] mt-0.5 hidden sm:block">Explore hairstyles</p>
          </Link>

          <Link to="/gallery" className="group bg-white rounded-xl sm:rounded-2xl border border-[#E6DCD3] p-4 sm:p-6 hover:shadow-md hover:-translate-y-0.5 transition-all text-center" data-testid="action-view-gallery">
            <div className="h-10 w-10 sm:h-12 sm:w-12 bg-[#F3EBE1] rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-3 group-hover:bg-[#C9A961] group-hover:text-white transition-colors">
              <Images className="h-5 w-5 sm:h-6 sm:w-6 text-[#3E2723] group-hover:text-white" />
            </div>
            <p className="text-xs sm:text-sm font-medium text-[#3E2723]">View Gallery</p>
            <p className="text-[9px] sm:text-xs text-[#786E66] mt-0.5 hidden sm:block">Browse trending styles</p>
          </Link>

          <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="group bg-white rounded-xl sm:rounded-2xl border border-[#E6DCD3] p-4 sm:p-6 hover:shadow-md hover:-translate-y-0.5 transition-all text-center" data-testid="action-chat-admin">
            <div className="h-10 w-10 sm:h-12 sm:w-12 bg-[#F3EBE1] rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-3 group-hover:bg-[#25D366] transition-colors">
              <MessageCircle className="h-5 w-5 sm:h-6 sm:w-6 text-[#3E2723] group-hover:text-white" />
            </div>
            <p className="text-xs sm:text-sm font-medium text-[#3E2723]">Chat Admin</p>
            <p className="text-[9px] sm:text-xs text-[#786E66] mt-0.5 hidden sm:block">Need help? Contact us</p>
          </a>
        </div>

        {/* Upcoming Appointment Highlight */}
        {upcomingBookings.length > 0 && (
          <div className="bg-gradient-to-r from-[#3E2723] to-[#5D4037] rounded-xl sm:rounded-2xl p-5 sm:p-6 mb-8 sm:mb-10 text-white" data-testid="upcoming-highlight">
            <p className="text-[#C9A961] text-xs font-medium tracking-wider uppercase mb-3">Upcoming Appointment</p>
            <div className="flex gap-4">
              {upcomingBookings[0].hairstyle_image && (
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden flex-shrink-0">
                  <img src={getPublicImageUrl(upcomingBookings[0].hairstyle_image)} alt="" className="w-full h-full object-cover" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h3 className="font-serif text-lg sm:text-xl mb-1 truncate">{upcomingBookings[0].hairstyle_name}</h3>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-white/70">
                  <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{upcomingBookings[0].appointment_date} at {upcomingBookings[0].appointment_time}</span>
                  <span>Ref: {upcomingBookings[0].booking_reference}</span>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${getColor(upcomingBookings[0].booking_status)}`}>
                    {formatStatus(upcomingBookings[0].booking_status)}
                  </span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${getColor(upcomingBookings[0].payment_status)}`}>
                    {formatStatus(upcomingBookings[0].payment_status)}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              {upcomingBookings[0].booking_status === 'pending_payment' && (
                <Link to={`/payment?booking=${upcomingBookings[0].id}`} className="bg-[#C9A961] text-[#3E2723] hover:bg-[#d4b876] rounded-full px-5 py-2 text-xs font-medium transition-all">
                  Make Payment
                </Link>
              )}
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="border border-white/30 text-white hover:bg-white/10 rounded-full px-5 py-2 text-xs font-medium transition-all inline-flex items-center gap-1.5">
                <MessageCircle className="h-3 w-3" /> Chat Admin
              </a>
            </div>
          </div>
        )}

        {/* Booking History */}
        <div className="mb-8 sm:mb-10" data-testid="booking-history-section">
          <h2 className="font-serif text-xl sm:text-2xl text-[#3E2723] mb-4">Booking History</h2>

          {/* Tabs */}
          <div className="flex gap-1 bg-[#F3EBE1] rounded-lg p-1 mb-5" data-testid="booking-tabs">
            {tabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex-1 py-2 px-3 rounded-md text-xs sm:text-sm font-medium transition-all ${
                  activeTab === tab.key ? 'bg-white text-[#3E2723] shadow-sm' : 'text-[#786E66] hover:text-[#3E2723]'
                }`}
                data-testid={`tab-${tab.key}`}
              >
                {tab.label} ({tab.count})
              </button>
            ))}
          </div>

          {/* Booking Cards */}
          {tabData[activeTab].length > 0 ? (
            <div className="space-y-3">
              {tabData[activeTab].map((booking, idx) => (
                <BookingCard
                  key={booking.id}
                  booking={booking}
                  idx={idx}
                  expanded={expandedBooking === booking.id}
                  onToggle={() => setExpandedBooking(expandedBooking === booking.id ? null : booking.id)}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-10 bg-white rounded-xl border border-[#E6DCD3]">
              <p className="text-[#786E66] mb-3 text-sm">
                {activeTab === 'upcoming' ? 'No upcoming appointments' :
                 activeTab === 'completed' ? 'No completed bookings yet' : 'No cancelled bookings'}
              </p>
              {activeTab === 'upcoming' && (
                <Link to="/gallery" className="bg-[#3E2723] text-white hover:bg-[#2A1A17] rounded-full px-6 py-2 text-sm inline-block transition-all">
                  Book Now
                </Link>
              )}
            </div>
          )}
        </div>

        {/* Recommended Styles */}
        {hairstyles.length > 0 && (
          <div data-testid="recommended-styles-section">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif text-xl sm:text-2xl text-[#3E2723]">Recommended Styles</h2>
              <Link to="/gallery" className="text-xs sm:text-sm text-[#C9A961] hover:text-[#3E2723] font-medium inline-flex items-center gap-1 transition-colors">
                View All <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
              {hairstyles.map((style, idx) => (
                <div key={style.id} className="bg-white rounded-xl border border-[#E6DCD3] overflow-hidden group" data-testid={`recommended-style-${idx}`}>
                  <div className="overflow-hidden bg-[#F3EBE1]">
                    {(style.cover_image || style.images?.[0]) ? (
                      <img src={getPublicImageUrl(style.cover_image || style.images[0])} alt={style.name} className="w-full h-32 sm:h-40 object-cover group-hover:scale-105 transition-transform duration-300" />
                    ) : (
                      <div className="w-full h-32 sm:h-40 flex items-center justify-center"><Crown className="h-8 w-8 text-[#E6DCD3]" /></div>
                    )}
                  </div>
                  <div className="p-3">
                    <h4 className="text-xs sm:text-sm font-medium text-[#3E2723] truncate">{style.name}</h4>
                    <p className="text-[10px] sm:text-xs text-[#786E66] mt-0.5">From ₦{style.price_ngn?.toLocaleString()}</p>
                    <button
                      onClick={() => navigate(`/booking?hairstyle=${style.id}`)}
                      className="w-full mt-2 bg-[#F3EBE1] text-[#3E2723] hover:bg-[#C9A961] hover:text-white rounded-full py-1.5 text-[10px] sm:text-xs font-medium transition-all"
                      data-testid={`recommended-book-btn-${idx}`}
                    >
                      Book Now
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default CustomerDashboard;
